import { flushSync } from "react-dom";
import {
  PDP_EXPAND_EASING,
  PDP_EXPAND_TRANSITION_MS,
  PDP_FORWARD_TRANSITION_MS,
} from "./pdpViewTransitionConstants";

type InputType = () => void;

export type ViewTransitionNavMode = "left-to-right" | "right-to-left" | "pdp-image-expand";

const LISTING_ID = "listing-transition";
const PDP_IMAGE_EXPAND_ID = "pdp-image-expand-transition";
const PRODUCT_ID = "product-transition";

// Tracks whether a view transition navigation is currently in progress.
// Used by useModal to avoid calling goBack() before the transition's
// history.push has executed.
let _isTransitionNavigating = false;

export function isViewTransitionNavigating(): boolean {
  return _isTransitionNavigating;
}

declare global {
  interface Document {
    startViewTransition(callback?: any): any;
  }
}
// Remove all transition styling before route change and add only what is needed
const removeAllStyling = () => {
  [LISTING_ID, PDP_IMAGE_EXPAND_ID, PRODUCT_ID, `${LISTING_ID}-ltr`].forEach((id) => {
    const styleElement = document.getElementById(id);
    if (styleElement) document.head.removeChild(styleElement);
  });
};

// Add transition styling for "right-to-left" navigation
const addTransitionSearch = () => {
  const style = document.createElement("style");
  style.type = "text/css";
  style.id = LISTING_ID;
  style.innerHTML = `
    /* New screen slides in from right */
    @keyframes slide-from-right {
      0% { transform: translateX(100%); }
      100% { transform: translateX(0%); }
    }

    /* Old screen darkens + blurs */
    @keyframes darken-and-blur {
      0% { filter: blur(0px) brightness(1); }
      20%{ filter: blur(0px) brightness(0.8); }  
      100% { filter: blur(4px) brightness(0); }
    }

    ::view-transition-old(root),
    ::view-transition-new(root) {
      will-change: transform, filter;
      backface-visibility: hidden;
    }

    /* Old stays visible but darkens */
    ::view-transition-old(root) {
      animation: 700ms ease both darken-and-blur;
    }

    /* New slides over it */
    ::view-transition-new(root) {
      animation: 700ms cubic-bezier(0.4, 0, 0.2, 1) both slide-from-right;
    }
  `;
  document.head.appendChild(style);
};
const addTransitionSearchLTR = () => {
  const style = document.createElement("style");
  style.type = "text/css";
  style.id = `${LISTING_ID}-ltr`;
  style.innerHTML = `
    @keyframes scale-up-from-behind {
      0% { 
        transform: scale(0.7) translate3d(0, 0, 0);
        opacity: 1;
      }
      100% { 
        transform: scale(1) translate3d(0, 0, 0);
        opacity: 1;
      }
    }
    
    @keyframes slide-to-right {
      0% { 
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
      100% { 
        transform: translate3d(100%, 0, 0);
        opacity: 1;
      }
    }

    /* Black background during transition */
    ::view-transition-group(root) {
      background-color: #000000;
    }

    ::view-transition-old(root),
    ::view-transition-new(root) {
      will-change: transform, opacity;
      backface-visibility: hidden;
      mix-blend-mode: normal;
    }

    /* Current screen slides out to the right */
    ::view-transition-old(root) {
      animation: 700ms cubic-bezier(0.4, 0, 0.2, 1) both slide-to-right;
      z-index: 2;
    }
    
    /* Previous screen scales up from behind */
    ::view-transition-new(root) {
      animation: 700ms cubic-bezier(0.4, 0, 0.2, 1) both scale-up-from-behind;
      z-index: 1;
    }

    /* PDP shared element names must not morph on back — release uses root slide only */
    ::view-transition-group(property-image-*),
    ::view-transition-old(property-image-*),
    ::view-transition-new(property-image-*),
    ::view-transition-group(property-content-*),
    ::view-transition-old(property-content-*),
    ::view-transition-new(property-content-*),
    ::view-transition-group(app-promotion-banner),
    ::view-transition-old(app-promotion-banner),
    ::view-transition-new(app-promotion-banner) {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
};

/** Search → PDP: card image morphs to gallery hero; page crossfades, no slide. */
const addTransitionPdpImageExpand = () => {
  const forwardMs = PDP_FORWARD_TRANSITION_MS;
  const expandMs = PDP_EXPAND_TRANSITION_MS;
  const style = document.createElement("style");
  style.type = "text/css";
  style.id = PDP_IMAGE_EXPAND_ID;
  style.innerHTML = `
    @keyframes pdp-search-fade-out {
      to { opacity: 0; }
    }

    @keyframes pdp-page-fade-in {
      0%, 55% { opacity: 0; }
      100% { opacity: 1; }
    }

    ::view-transition-group(root) {
      animation-duration: ${forwardMs}ms;
    }

    ::view-transition-old(root),
    ::view-transition-new(root) {
      will-change: opacity;
      backface-visibility: hidden;
      mix-blend-mode: normal;
    }

    ::view-transition-old(root) {
      animation: ${forwardMs}ms ${PDP_EXPAND_EASING} both pdp-search-fade-out;
      z-index: 1;
    }

    ::view-transition-new(root) {
      animation: ${forwardMs}ms ${PDP_EXPAND_EASING} both pdp-page-fade-in;
      z-index: 0;
    }

    /* Shared-element expand — must be top-level (not under body); VT pseudo-elements live on :root */
    ::view-transition-group(property-image-*) {
      z-index: 1;
      animation-duration: ${expandMs}ms !important;
      animation-timing-function: ${PDP_EXPAND_EASING} !important;
    }

    ::view-transition-old(property-image-*),
    ::view-transition-new(property-image-*) {
      animation-duration: ${expandMs}ms !important;
      animation-timing-function: ${PDP_EXPAND_EASING} !important;
      object-fit: cover;
      mix-blend-mode: normal;
      height: 100%;
      width: 100%;
    }

    ::view-transition-group(property-content-*) {
      z-index: 2;
      animation-duration: ${expandMs}ms !important;
      animation-timing-function: ${PDP_EXPAND_EASING} !important;
    }

    ::view-transition-old(property-content-*),
    ::view-transition-new(property-content-*) {
      animation-duration: ${expandMs}ms !important;
      animation-timing-function: ${PDP_EXPAND_EASING} !important;
      mix-blend-mode: normal;
      height: 100%;
      width: 100%;
      overflow: hidden;
    }

    ::view-transition-group(pdp-header) {
      z-index: 3;
      animation: none;
    }

    ::view-transition-group(app-promotion-banner) {
      z-index: 4;
      animation: none;
    }

    ::view-transition-old(app-promotion-banner),
    ::view-transition-new(app-promotion-banner) {
      animation: none;
      mix-blend-mode: normal;
    }
  `;
  document.head.appendChild(style);
};

// Main utility function
export default function viewTransitionNavUtility(
  updateCb: InputType,
  page: ViewTransitionNavMode,
): void {
  removeAllStyling();

  // Check for View Transition API support
  if (!document.startViewTransition || (typeof __DESKTOP__ !== "undefined" && __DESKTOP__)) {
    updateCb();
    return;
  }

  if (page === "pdp-image-expand") {
    addTransitionPdpImageExpand();
  } else if (page === "right-to-left") {
    addTransitionSearch();
  } else {
    addTransitionSearchLTR();
    document.body.classList.add("pdp-back-vt");
  }

  // Start the view transition
  try {
    // Lock the viewport size during the transition
    const originalViewportWidth = window.innerWidth;
    const originalViewportHeight = window.innerHeight;

    const transition = document.startViewTransition(() => {
      // Ensure the viewport size hasn't changed before updating the DOM
      if (
        window.innerWidth !== originalViewportWidth ||
        window.innerHeight !== originalViewportHeight
      ) {
        console.warn("Viewport size changed during transition. Skipping transition.");
        _isTransitionNavigating = false;
        updateCb();
        return;
      }

      if (page === "pdp-image-expand" || page === "left-to-right") {
        flushSync(() => {
          updateCb();
        });
      } else {
        updateCb();
      }
      _isTransitionNavigating = false;
    });

    _isTransitionNavigating = true;

    // Handle successful transition completion
    transition.finished
      .then(() => {
        removeAllStyling();
        document.body.classList.remove("pdp-back-vt");
      })
      .catch((error: unknown) => {
        if (
          error instanceof Error &&
          error.name === "InvalidStateError" &&
          error.message.includes("viewport size changed")
        ) {
          console.warn("View transition skipped due to viewport size change.");
        } else {
          console.error("View transition failed:", error);
        }
        removeAllStyling();
        document.body.classList.remove("pdp-back-vt");
      });
  } catch (error) {
    // Handle initialization errors
    if (
      error instanceof Error &&
      error.name === "InvalidStateError" &&
      error.message.includes("viewport size changed")
    ) {
      console.warn("View transition skipped due to viewport size change.");
    } else {
      console.error("View transition initialization failed:", error);
    }
    _isTransitionNavigating = false;
    updateCb();
    removeAllStyling();
    document.body.classList.remove("pdp-back-vt");
  }
}
